import { Box, IconButton, Typography } from "@mui/material";
import { Close, HighlightAlt } from "@mui/icons-material";
import { ReactNode } from "react";
import { PortalTooltip } from "../../../../../helpers";

export interface MapToolbarProps {
	jobCount: number;
	onClose: () => void;
	actions?: ReactNode;
	lassoMode?: boolean;
	selectedFeatureIds?: Set<string>;
	onToggleLasso?: () => void;
}

export function MapToolbar({
	jobCount,
	onClose,
	actions,
	lassoMode,
	selectedFeatureIds,
	onToggleLasso,
}: MapToolbarProps) {
	const hasSelectedFeatures = selectedFeatureIds && selectedFeatureIds.size > 0;

	// Count selected polygons vs points
	const getSelectionText = () => {
		if (!selectedFeatureIds || selectedFeatureIds.size === 0) return null;
		let polygons = 0;
		let points = 0;
		selectedFeatureIds.forEach((id) => {
			if (id.startsWith("points-")) {
				points++;
			} else {
				polygons++;
			}
		});
		const parts = [];
		if (polygons > 0) parts.push(`${polygons} polygon${polygons !== 1 ? "s" : ""}`);
		if (points > 0) parts.push(`${points} point${points !== 1 ? "s" : ""}`);
		return `Selected: ${parts.join(" and ")}`;
	};

	const getSelectButtonTooltip = () => {
		if (hasSelectedFeatures) return "Clear selection";
		if (lassoMode) return "Click to add points, click first point to close";
		return "Select features";
	};

	return (
		<Box
			className="map-toolbar"
			sx={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				padding: 1.5,
				borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
				flexShrink: 0,
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
				<Typography variant="h6">
					{jobCount} {jobCount === 1 ? "Job" : "Jobs"}
				</Typography>
				{getSelectionText() && (
					<Typography variant="body2" color="error.main">
						{getSelectionText()}
					</Typography>
				)}
			</Box>
			<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
				{actions}
				<PortalTooltip title={getSelectButtonTooltip()}>
					<IconButton
						onClick={onToggleLasso}
						aria-label="select features"
						size="small"
						sx={{
							color: hasSelectedFeatures ? "error.main" : lassoMode ? "#2196f3" : undefined,
						}}
					>
						<HighlightAlt />
					</IconButton>
				</PortalTooltip>
				<IconButton onClick={onClose} aria-label="close map" size="small">
					<Close />
				</IconButton>
			</Box>
		</Box>
	);
}
